import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatEUR } from '@/lib/revenuePricing';

interface TierRow {
  tier: string;
  count: number;
  mrr: number;
  arpu: number;
}

interface TierBreakdownProps {
  data: TierRow[];
}

const TIER_COLORS: Record<string, string> = {
  starter: 'hsl(var(--muted-foreground))',
  pro: 'hsl(var(--primary))',
  enterprise: 'hsl(var(--accent))',
};

const TIER_LABELS: Record<string, string> = {
  starter: 'Starter',
  pro: 'Pro',
  enterprise: 'Enterprise',
};

export function TierBreakdown({ data }: TierBreakdownProps) {
  const totalMRR = data.reduce((sum, t) => sum + t.mrr, 0);
  const totalCount = data.reduce((sum, t) => sum + t.count, 0);

  return (
    <Card className="app-tile">
      <CardHeader>
        <CardTitle className="text-lg">Tier Breakdown</CardTitle>
        <CardDescription>MRR contribution per subscription tier</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="h-[220px] flex items-center justify-center">
            {data.length === 0 ? (
              <p className="text-sm text-muted-foreground">No paying subs yet</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data}
                    dataKey="mrr"
                    nameKey="tier"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={85}
                    paddingAngle={2}
                  >
                    {data.map((entry) => (
                      <Cell
                        key={entry.tier}
                        fill={TIER_COLORS[entry.tier] ?? 'hsl(var(--muted))'}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 'var(--radius)',
                      fontSize: 12,
                    }}
                    formatter={(value: number, name: string) => [
                      formatEUR(value),
                      TIER_LABELS[name] ?? name,
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          <div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tier</TableHead>
                  <TableHead className="text-right">Subs</TableHead>
                  <TableHead className="text-right">MRR</TableHead>
                  <TableHead className="text-right">ARPU</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((row) => (
                  <TableRow key={row.tier}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span
                          className="h-2 w-2 rounded-full"
                          style={{ backgroundColor: TIER_COLORS[row.tier] ?? 'hsl(var(--muted))' }}
                        />
                        <span className="font-medium">{TIER_LABELS[row.tier] ?? row.tier}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{row.count}</TableCell>
                    <TableCell className="text-right font-semibold">{formatEUR(row.mrr)}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{formatEUR(row.arpu)}</TableCell>
                  </TableRow>
                ))}
                {data.length > 0 && (
                  <TableRow className="border-t-2">
                    <TableCell className="font-semibold">Total</TableCell>
                    <TableCell className="text-right font-semibold">{totalCount}</TableCell>
                    <TableCell className="text-right font-semibold">{formatEUR(totalMRR)}</TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {totalCount > 0 ? formatEUR(totalMRR / totalCount) : '—'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
